import { useFormik } from "formik";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Link, useHistory, withRouter } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { object, string } from "yup";

import AsyncSelectField from "../../../Components/Common/AsyncSelectField";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useCreateUserSubcategory,
  useUserCategories,
} from "../../../hooks/useUserCategories";

/**
 * Institution creation.
 *
 * An institution always belongs to one institution category, so the category
 * is required here — unlike a category, which only needs a name.
 */
const AddSubCategory = (props) => {
  document.title = props.t("Créer une institution | Le Conseiller du BTP");

  const history = useHistory();
  const createMutation = useCreateUserSubcategory();
  const categoriesQ = useUserCategories();
  const categories = categoriesQ.data || [];

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: "",
      category_id: "",
    },

    validationSchema: object().shape({
      name: string().required(props.t("Entrer le nom de l'institution")),
      category_id: string().required(
        props.t("Choisissez la catégorie d'institutions")
      ),
    }),

    onSubmit: async (values) => {
      try {
        await createMutation.mutateAsync({
          name: values.name,
          category_id: Number(values.category_id),
        });
        validation.resetForm();
        history.push("/utilisateurs-institutions");
      } catch (_) {
        // handled by toast in hook
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t("Créer une institution")}
          pageTitle={props.t("Utilisateurs")}
        />

        <Row>
          <Col lg={12}>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <Card>
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="name">
                      {props.t("Nom de l'institution")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      className="form-control"
                      placeholder={props.t("Ex : Ministère de la Justice")}
                      name="name"
                      value={validation.values.name}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      invalid={Boolean(
                        validation.errors.name && validation.touched.name
                      )}
                    />
                    {validation.errors.name && validation.touched.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <AsyncSelectField
                    label={props.t("Catégorie d'institutions")}
                    name="category_id"
                    value={validation.values.category_id}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    options={categories}
                    optionLabelKey="name"
                    placeholder={props.t("Sélectionner la catégorie")}
                    required
                    isLoading={categoriesQ.isLoading}
                    isError={categoriesQ.isError}
                    onRetry={categoriesQ.refetch}
                    errorText={props.t(
                      "Impossible de charger les catégories d'institutions."
                    )}
                    emptyText={props.t(
                      "Aucune catégorie d'institutions n'existe encore. Créez-en une d'abord."
                    )}
                    invalid={Boolean(
                      validation.errors.category_id &&
                        validation.touched.category_id
                    )}
                    feedback={validation.errors.category_id}
                    hint={props.t(
                      "L'institution sera rattachée à cette catégorie."
                    )}
                  />
                </CardBody>
              </Card>

              <div className="text-end mb-3">
                <Link
                  to="/utilisateurs-institutions"
                  className="btn btn-primary me-2"
                >
                  {props.t("Retour")}
                </Link>
                <button
                  type="submit"
                  className="btn btn-warning w-sm m-2"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending && (
                    <Spinner size="sm" className="me-2" />
                  )}
                  {props.t("Ajouter")}
                </button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

AddSubCategory.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(AddSubCategory));
